import React, { useState, useEffect } from "react";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import { UserPlus, UserCog, FileText, Trash2 } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useI18n } from "@/i18n";
import { useLookupValues } from "@/lib/platformConfig";
import { useAuth } from "@/lib/auth";
import { DatePicker } from "@/components/ui/calendar";

function StaffDocsTab({ staff }) {
  const { t } = useI18n();
  const lookup = useLookupValues("staff_document_kinds");
  const docKinds = (lookup.data?.values || []).map((row) => ({ value: row.value, label: row.label }));
  const options = docKinds;
  const normalize = (value) => String(value || "").trim().toLowerCase();
  const isOtherOption = (value) => {
    const key = normalize(value);
    return key === "other" || key === "others" || key.startsWith("other_");
  };
  const otherOption = options.find((entry) => isOtherOption(entry.value) || normalize(entry.label) === "other");
  const [addOpen, setAddOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [docForm, setDocForm] = useState({
    subject_type: "staff",
    subject_id: "",
    kind: "",
    other_kind: "",
    number: "",
    issue_date: "",
    expiry_date: "",
    notes: "",
  });
  const qc = useQueryClient();

  useEffect(() => {
    if (!options.length) return;
    setDocForm((prev) =>
      options.some((entry) => entry.value === prev.kind)
        ? prev
        : { ...prev, kind: options[0].value },
    );
  }, [options]);

  const { data: docs = [], isLoading } = useQuery({
    queryKey: ["staff-docs"],
    queryFn: async () => unwrap(await api.get("/documents?subject_type=staff")),
  });

  const showOtherInput = isOtherOption(docForm.kind) || (otherOption && docForm.kind === otherOption.value);

  const createMut = useMutation({
    mutationFn: async () => {
      const payload = {
        ...docForm,
        other_kind: showOtherInput ? docForm.other_kind.trim() : "",
        issue_date: docForm.issue_date || null,
        expiry_date: docForm.expiry_date || null,
      };
      return unwrap(await api.post("/documents", payload));
    },
    onSuccess: () => {
      toast.success("Document added");
      qc.invalidateQueries({ queryKey: ["staff-docs"] });
      setAddOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const deleteMut = useMutation({
    mutationFn: async (docId) =>
      unwrap(await api.delete(`/documents/${docId}`)),
    onSuccess: () => {
      toast.success("Document deleted");
      qc.invalidateQueries({ queryKey: ["staff-docs"] });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openAdd = (staffUser) => {
    if (!staffUser) return;
    setSelectedStaff(staffUser);
    setDocForm((prev) => ({
      ...prev,
      subject_id: staffUser.id,
      other_kind: "",
      kind:
        options.some((entry) => entry.value === prev.kind)
          ? prev.kind
          : (options[0]?.value || ""),
    }));
    setAddOpen(true);
  };

  const getKindLabel = (doc) =>
    options.find((k) => k.value === doc.kind)?.label || doc.kind;

  const getExpiryStatus = (expiryDate) => {
    if (!expiryDate) return null;
    const exp = new Date(expiryDate);
    const now = new Date();
    const days = Math.ceil((exp - now) / (1000 * 60 * 60 * 24));
    if (days < 0)
      return <Badge className="bg-red-500 text-white">Expired</Badge>;
    if (days <= 30)
      return <Badge className="bg-orange-500 text-white">{days}d</Badge>;
    if (days <= 60)
      return <Badge className="bg-yellow-500 text-black">{days}d</Badge>;
    return <Badge className="bg-green-500 text-white">{days}d</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          onClick={() => openAdd(staff[0])}
          disabled={!staff.length || !options.length}
          className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
          data-testid="add-doc-btn"
        >
          <FileText className="h-4 w-4 mr-1.5" /> Add document
        </Button>
      </div>

      {isLoading ? (
        <Skeleton className="h-32" />
      ) : docs.length === 0 ? (
        <p className="text-sm text-[var(--text-muted)] text-center py-8">
          No staff documents found
        </p>
      ) : (
        <table className="w-full text-sm grid-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-3">Staff</th>
              <th className="text-left px-4 py-3">Kind</th>
              <th className="text-left px-4 py-3">Number</th>
              <th className="text-left px-4 py-3">Expiry</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {docs.map((doc) => (
              <tr key={doc.id} data-testid={`doc-row-${doc.id}`}>
                <td className="px-4 py-3 font-medium">{doc.subject_name}</td>
                <td className="px-4 py-3">{getKindLabel(doc)}</td>
                <td className="px-4 py-3 font-mono-num text-xs">
                  {doc.number || "—"}
                </td>
                <td className="px-4 py-3 font-mono-num text-xs">
                  {doc.expiry_date || "—"}
                </td>
                <td className="px-4 py-3">
                  {getExpiryStatus(doc.expiry_date)}
                </td>
                <td className="px-4 py-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteMut.mutate(doc.id)}
                    data-testid={`delete-doc-${doc.id}`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <Dialog open={addOpen} onOpenChange={setAddOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              Add Document — {selectedStaff?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">
                Document Type
              </Label>
              <Select
                value={docForm.kind}
                onValueChange={(v) =>
                  setDocForm((prev) => ({
                    ...prev,
                    kind: v,
                    other_kind: isOtherOption(v) ? prev.other_kind : "",
                  }))
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select document type" />
                </SelectTrigger>
                <SelectContent>
                  {options.map((k) => (
                    <SelectItem key={k.value} value={k.value}>
                      {k.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!options.length && <p className="mt-1 text-xs text-[var(--text-muted)]">No document kinds configured in Platform Settings.</p>}
            </div>
            {showOtherInput && (
              <div>
                <Label className="text-xs uppercase font-semibold">
                  Other Document Type
                </Label>
                <Input
                  value={docForm.other_kind}
                  onChange={(e) =>
                    setDocForm({ ...docForm, other_kind: e.target.value })
                  }
                  className="mt-1"
                  data-testid="doc-other-kind"
                  placeholder="Enter document type"
                />
              </div>
            )}
            <div>
              <Label className="text-xs uppercase font-semibold">
                Document Number
              </Label>
              <Input
                value={docForm.number}
                onChange={(e) =>
                  setDocForm({ ...docForm, number: e.target.value })
                }
                className="mt-1"
                data-testid="doc-number"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Issue Date
              </Label>
              <DatePicker
                value={docForm.issue_date}
                onChange={(date) =>
                  setDocForm({ ...docForm, issue_date: date })
                }
                testId="doc-issue-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Expiry Date
              </Label>
              <DatePicker
                value={docForm.expiry_date}
                onChange={(date) =>
                  setDocForm({ ...docForm, expiry_date: date })
                }
                testId="doc-expiry-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">Notes</Label>
              <Input
                value={docForm.notes}
                onChange={(e) =>
                  setDocForm({ ...docForm, notes: e.target.value })
                }
                className="mt-1"
                data-testid="doc-notes"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => createMut.mutate()}
              disabled={
                !docForm.kind ||
                (showOtherInput && !docForm.other_kind.trim()) ||
                createMut.isPending
              }
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="save-doc"
            >
              {createMut.isPending ? "Saving..." : "Save document"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default function Staff() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [activeTab, setActiveTab] = useState("staff");
  const { data, isLoading } = useQuery({
    queryKey: ["staff"],
    queryFn: async () => unwrap(await api.get("/staff")),
  });
  const qc = useQueryClient();

  const assignableRoles = React.useMemo(
    () =>
      (user?.assignable_roles || []).filter(
        (role) => !["platform_admin", "tenant_owner", "guardian"].includes(role)
      ),
    [user?.assignable_roles]
  );
  const [inviteForm, setInviteForm] = useState({
    email: "",
    full_name: "",
    phone: "",
    role: "",
    password: "",
  });
  const [profileForm, setProfileForm] = useState({
    employee_no: "",
    date_of_joining: "",
    designation_id: "",
    department_id: "",
    employment_type: "",
    ctc_paise_annual: 0,
  });
  const employmentLookup = useLookupValues("staff_employment_types");
  const employmentOptions = React.useMemo(
    () =>
      (employmentLookup.data?.values || [])
        .filter((row) => row?.value)
        .map((row) => ({ value: row.value, label: row.label || row.value })),
    [employmentLookup.data?.values]
  );

  useEffect(() => {
    if (!inviteForm.role && assignableRoles.length)
      setInviteForm((prev) => ({
        ...prev,
        role: assignableRoles[0],
      }));
  }, [assignableRoles, inviteForm.role]);
  useEffect(() => {
    if (!profileForm.employment_type && employmentOptions.length)
      setProfileForm((prev) => ({
        ...prev,
        employment_type: employmentOptions[0].value,
      }));
  }, [employmentOptions, profileForm.employment_type]);

  const { data: departments = [] } = useQuery({
    queryKey: ["hr-departments"],
    queryFn: async () => unwrap(await api.get("/hr/departments")),
    enabled: profileOpen,
  });
  const { data: designations = [] } = useQuery({
    queryKey: ["hr-designations"],
    queryFn: async () => unwrap(await api.get("/hr/designations")),
    enabled: profileOpen,
  });
  const { data: profiles = [] } = useQuery({
    queryKey: ["staff-profiles"],
    queryFn: async () => unwrap(await api.get("/hr/staff-profiles")),
    enabled: profileOpen || activeTab === "documents",
  });

  useEffect(() => {
    if (profileOpen && selectedUser) {
      const existing = profiles.find((p) => p.user_id === selectedUser.id);
      if (existing) {
        setProfileForm({
          employee_no: existing.employee_no || "",
          date_of_joining: existing.date_of_joining || "",
          designation_id: existing.designation_id || "",
          department_id: existing.department_id || "",
          employment_type: existing.employment_type || employmentOptions[0]?.value || "",
          ctc_paise_annual: existing.ctc_paise_annual || 0,
        });
      } else {
        setProfileForm({
          employee_no: "",
          date_of_joining: "",
          designation_id: "",
          department_id: "",
          employment_type: employmentOptions[0]?.value || "",
          ctc_paise_annual: 0,
        });
      }
    }
  }, [profileOpen, selectedUser, profiles, employmentOptions]);

  const inviteMut = useMutation({
    mutationFn: async () => unwrap(await api.post("/staff/invite", inviteForm)),
    onSuccess: () => {
      toast.success("Staff invited");
      qc.invalidateQueries({ queryKey: ["staff"] });
      setInviteOpen(false);
      setInviteForm({
        email: "",
        full_name: "",
        phone: "",
        role: assignableRoles[0] || "",
        password: "",
      });
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const profileMut = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.put(`/hr/staff-profiles/${selectedUser.id}`, profileForm),
      ),
    onSuccess: () => {
      toast.success("HR profile saved");
      qc.invalidateQueries({ queryKey: ["staff-profiles"] });
      setProfileOpen(false);
    },
    onError: (e) => toast.error(errMsg(e)),
  });

  const openProfile = (user) => {
    setSelectedUser(user);
    setProfileOpen(true);
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="staff-page">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-6">
        <div>
          <h1 className="font-display text-3xl font-bold text-[var(--text-primary)]">
            {t("management.staffTitle")}
          </h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            {t("management.staffSubtitle")}
          </p>
        </div>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild>
            <Button
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="invite-staff-btn"
            >
              <UserPlus className="h-4 w-4 mr-1.5" />{" "}
              {t("management.inviteStaff")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">
                Invite staff member
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div>
                <Label className="text-xs uppercase font-semibold">
                  Full name
                </Label>
                <Input
                  value={inviteForm.full_name}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, full_name: e.target.value })
                  }
                  className="mt-1"
                  data-testid="staff-name"
                />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold">Email</Label>
                <Input
                  type="email"
                  value={inviteForm.email}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, email: e.target.value })
                  }
                  className="mt-1"
                  data-testid="staff-email"
                />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold">Phone</Label>
                <Input
                  value={inviteForm.phone}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, phone: e.target.value })
                  }
                  className="mt-1"
                  data-testid="staff-phone"
                />
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold">Role</Label>
                <Select
                  value={inviteForm.role}
                  onValueChange={(v) => setInviteForm({ ...inviteForm, role: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {assignableRoles.map((role) => (
                      <SelectItem key={role} value={role}>
                        {role.replaceAll("_", " ")}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase font-semibold">
                  Temporary password
                </Label>
                <Input
                  type="password"
                  autoComplete="new-password"
                  minLength={8}
                  value={inviteForm.password}
                  onChange={(e) =>
                    setInviteForm({ ...inviteForm, password: e.target.value })
                  }
                  className="mt-1"
                  data-testid="staff-password"
                />
                <p className="mt-1 text-xs text-[var(--text-muted)]">
                  Use at least 8 characters; the staff member must set a strong
                  password at first sign-in.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={() => inviteMut.mutate()}
                disabled={
                  !inviteForm.email ||
                  !inviteForm.full_name ||
                  !inviteForm.phone ||
                  !inviteForm.role ||
                  !inviteForm.password ||
                  inviteMut.isPending
                }
                className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
                data-testid="submit-staff"
              >
                {inviteMut.isPending ? "Inviting..." : "Send invite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-4">
          <TabsTrigger value="staff">Staff List</TabsTrigger>
          <TabsTrigger value="documents">Documents & Certificates</TabsTrigger>
        </TabsList>

        <TabsContent value="staff">
          <Card className="border-[var(--subtle)]">
            <CardContent className="p-0">
              {isLoading ? (
                <Skeleton className="h-64" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm grid-table">
                    <thead>
                      <tr>
                        <th className="text-left px-4 py-3">Name</th>
                        <th className="text-left px-4 py-3">Email</th>
                        <th className="text-left px-4 py-3">Phone</th>
                        <th className="text-left px-4 py-3">Roles</th>
                        <th className="text-left px-4 py-3">HR Profile</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(data || []).map((u) => {
                        const profile = profiles.find(
                          (p) => p.user_id === u.id,
                        );
                        return (
                          <tr key={u.id} data-testid={`staff-row-${u.email}`}>
                            <td className="px-4 py-3 font-medium">
                              {u.full_name}
                            </td>
                            <td className="px-4 py-3 font-mono-num text-xs">
                              {u.email}
                            </td>
                            <td className="px-4 py-3 font-mono-num text-xs">
                              {u.phone}
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex flex-wrap gap-1">
                                {(u.role_assignments || []).map((ra, i) => (
                                  <Badge
                                    key={i}
                                    className="bg-[var(--brand)] hover:bg-[var(--brand)] text-white capitalize"
                                  >
                                    {ra.role.replace("_", " ")}
                                  </Badge>
                                ))}
                              </div>
                            </td>
                            <td className="px-4 py-3">
                              {profile ? (
                                <span className="text-xs text-[var(--text-muted)]">
                                  {profile.employee_no} · Joined{" "}
                                  {profile.date_of_joining}
                                </span>
                              ) : (
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => openProfile(u)}
                                  data-testid={`profile-btn-${u.email}`}
                                >
                                  <UserCog className="h-4 w-4 mr-1" /> Setup
                                </Button>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="documents">
          <Card className="border-[var(--subtle)]">
            <CardContent className="p-4">
              <StaffDocsTab staff={data || []} />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <Dialog open={profileOpen} onOpenChange={setProfileOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-display">
              HR Profile — {selectedUser?.full_name}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs uppercase font-semibold">
                Employee No.
              </Label>
              <Input
                value={profileForm.employee_no}
                onChange={(e) =>
                  setProfileForm({
                    ...profileForm,
                    employee_no: e.target.value,
                  })
                }
                className="mt-1"
                data-testid="emp-no"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Date of Joining
              </Label>
              <DatePicker
                value={profileForm.date_of_joining}
                onChange={(date) =>
                  setProfileForm({ ...profileForm, date_of_joining: date })
                }
                testId="join-date"
              />
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Department
              </Label>
              <Select
                value={profileForm.department_id}
                onValueChange={(v) =>
                  setProfileForm({ ...profileForm, department_id: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select department" />
                </SelectTrigger>
                <SelectContent>
                  {(departments || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Designation
              </Label>
              <Select
                value={profileForm.designation_id}
                onValueChange={(v) =>
                  setProfileForm({ ...profileForm, designation_id: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select designation" />
                </SelectTrigger>
                <SelectContent>
                  {(designations || []).map((d) => (
                    <SelectItem key={d.id} value={d.id}>
                      {d.title}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Employment Type
              </Label>
              <Select
                value={profileForm.employment_type}
                onValueChange={(v) =>
                  setProfileForm({ ...profileForm, employment_type: v })
                }
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {employmentOptions.map((entry) => (
                    <SelectItem key={entry.value} value={entry.value}>
                      {entry.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {!employmentOptions.length && <p className="mt-1 text-xs text-[var(--text-muted)]">No employment types configured in Platform Settings.</p>}
            </div>
            <div>
              <Label className="text-xs uppercase font-semibold">
                Annual CTC (paise)
              </Label>
              <Input
                type="number"
                value={profileForm.ctc_paise_annual}
                onChange={(e) =>
                  setProfileForm({
                    ...profileForm,
                    ctc_paise_annual: parseInt(e.target.value) || 0,
                  })
                }
                className="mt-1"
                data-testid="ctc"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => profileMut.mutate()}
              disabled={
                !profileForm.employee_no ||
                !profileForm.date_of_joining ||
                profileMut.isPending
              }
              className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white"
              data-testid="save-profile"
            >
              {profileMut.isPending ? "Saving..." : "Save profile"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
